import { BoxType, GroupPeopleType, isRosterCellRole } from '@lite-workspace/dto-fotoclass-templates';
import type { CustomGroupTemplateBox, PsdGroupComposite } from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableData, DrawableGroupData } from '../../interfaces/drawable-data.interface';
import { buildBoxPassthroughMeta, carriedBoxFields, mergeBoxPassthroughMeta } from '../passthrough';
import { buildDrawableEffects, mergeDrawableEffects } from './effects';
import { rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder, NormalizerContext } from './codec-contract';

const KNOWN_PEOPLE_TYPES = new Set<string>(Object.values(GroupPeopleType));

/** Unrecognized peopleType falls back to FREE (excluded from vignette calc) — not a blind cast. */
function resolvePeopleType(value: string, ctx: NormalizerContext): GroupPeopleType {
  if (KNOWN_PEOPLE_TYPES.has(value)) return value as GroupPeopleType;
  ctx.warn(`params.peopleType unknown value "${value}", falling back to "${GroupPeopleType.FREE}"`);
  return GroupPeopleType.FREE;
}

/**
 * Принятый по фоллбеку род — необязательное поле: отсутствие это НЕ ошибка и не должно давать warn.
 * Пустой объект вместо ключа сохраняет прежнюю форму params у групп без пометки.
 */
function adoptedParams(value: string | undefined, ctx: NormalizerContext): { adoptedPeopleType?: GroupPeopleType } {
  return value === undefined ? {} : { adoptedPeopleType: resolvePeopleType(value, ctx) };
}

/** Absent block stays absent: a plain group keeps its persisted params byte-for-byte. */
function psdGroupParams(value: PsdGroupComposite | undefined): { psdGroup?: PsdGroupComposite } {
  return value === undefined ? {} : { psdGroup: value };
}

/**
 * Инвариант ростерной ячейки: у её рамок типа кадра не бывает. Роль ребёнка знает только родитель,
 * поэтому чистка живёт здесь, а не в image-box: кодек рамки о своей группе не знает. Гейт закрывает
 * ввод мимо UI — импорт PSD, накат дизайн-пака, прямой PATCH: сборка сажает человека группы, не глядя
 * на роль ребёнка, и оставленный тип напечатал бы портрет ученика под именем педагога.
 *
 * ОДНА чистка на обе стороны кодека: на чтении она спасает картинку редактора, на записи — персист,
 * иначе модель, которую редактор отправит на сервер, сочетание всё равно унесёт. Снятие громкое:
 * молчаливая правка чужой разметки не отличима от потери данных.
 */
function stripRosterFrameRoles(
  drawables: readonly DrawableData[],
  peopleType: string,
  ctx: NormalizerContext,
): readonly DrawableData[] {
  if (!isRosterCellRole(peopleType)) return drawables;
  return drawables.map(d => {
    if (d.type !== 'image' || d.frameRole === undefined) return d;
    ctx.warn(rosterFrameRoleDropped(d.frameRole, peopleType));
    const { frameRole: _role, ...rest } = d;
    return rest;
  });
}

/** Одно сообщение на все формы: снятие роли читают логи прода, и разойтись текст не должен. */
export function rosterFrameRoleDropped(frameRole: string, peopleType: string): string {
  return `params.frameRole "${frameRole}" on an image inside a "${peopleType}" group, dropping — the cell seats a person`;
}

export const decode: BoxDecoder<CustomGroupTemplateBox, DrawableGroupData> = (box, ctx) => {
  // Род резолвится ОДИН раз: второй вызов повторил бы warn о нераспознанном значении.
  const peopleType = resolvePeopleType(box.params.peopleType, ctx);
  return {
    id: box.id,
    type: 'group',
    ...carriedBoxFields(box),
    order: 0, // dispatcher проставляет реальный order по индексу в collage — не забота кодека
    canResize: true,
    opacity: box.opacity,
    blendMode: box.blendMode,
    visible: box.visible,
    locked: box.locked,
    rotation: box.rotation,
    effects: buildDrawableEffects(box.effects),
    draggable: box.draggable,
    params: {
      personId: box.params.personId,
      peopleType,
      ...adoptedParams(box.params.adoptedPeopleType, ctx),
      ...psdGroupParams(box.params.psdGroup),
      ...(box.params.ownerCard === true && { ownerCard: true }),
      ...(box.params.packOwnerCard === true && { packOwnerCard: true }),
    },
    drawableObjects: stripRosterFrameRoles(ctx.decodeBoxList(box.boxes), peopleType, ctx),
    naturalAligns: { container: rectFromBox(box.rect, ctx) },
    templateMeta: buildBoxPassthroughMeta(box),
  };
};

export const encode: BoxEncoder<DrawableGroupData, CustomGroupTemplateBox> = (drawable, ctx) => {
  // Род резолвится ОДИН раз и до чистки: она судит по тому же значению, что уедет в персист.
  const peopleType = resolvePeopleType(drawable.params.peopleType, ctx);
  const box: CustomGroupTemplateBox = {
    type: BoxType.GROUP,
    id: drawable.id,
    ...carriedBoxFields(drawable),
    rect: rectToBox(drawable.naturalAligns.container, ctx),
    rotation: mergeRotation(drawable, ctx),
    opacity: drawable.opacity,
    blendMode: drawable.blendMode,
    visible: drawable.visible,
    locked: drawable.locked,
    effects: mergeDrawableEffects(
      drawable.effects,
      drawable.templateMeta?.rawEffects,
      drawable.layerStyleRendering === 'live' ? 'typed' : undefined,
    ),
    draggable: drawable.draggable,
    params: {
      personId: drawable.params.personId,
      peopleType,
      ...adoptedParams(drawable.params.adoptedPeopleType, ctx),
      ...psdGroupParams(drawable.params.psdGroup),
      ...(drawable.params.ownerCard === true && { ownerCard: true }),
      ...(drawable.params.packOwnerCard === true && { packOwnerCard: true }),
    },
    boxes: [...ctx.encodeDrawableList(stripRosterFrameRoles(drawable.drawableObjects, peopleType, ctx))],
  };

  return mergeBoxPassthroughMeta(box, drawable.templateMeta);
};
